import { rd } from '@passionware/monads';
import { useState } from 'react';
import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import { useBrowsingWorkspaceRoutes } from 'v5/core/app-scope/useBrowsingWorkspaceRoutes';
import type { WithServices } from 'v5/platform/ts/services';
import type { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import type { WithGlobalSettingsService } from 'v5/services/internal/GlobalSettingsService/GlobalSettingsService';
import type { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { ResourceDuplicatesFooter } from './ResourceDuplicatesFooter';
import {
  type ResourceDuplicateCandidate,
  useResourceDuplicates
} from './useResourceDuplicates';

export interface ResourceDuplicatesFooterConnectedProps
  extends WithServices<
    [WithGlobalSettingsService, WithResourceService, WithAppScopeService]
  > {
  typedName: string;
  /** The complete name to look for; `null` while it is incomplete. */
  candidate: ResourceDuplicateCandidate | null;
  organizationId: string;
  workspaces: EnterpriseWorkspace[] | undefined;
}

/**
 * Warns about resources of every type and workspace whose name contains the typed
 * one. Dismissing hides the current set of matches only; a different set
 * shows up again.
 */
export function ResourceDuplicatesFooterConnected({
  typedName,
  candidate,
  organizationId,
  workspaces,
  services
}: ResourceDuplicatesFooterConnectedProps) {
  const routes = useBrowsingWorkspaceRoutes(services);
  const matches = rd.tryGet(useResourceDuplicates(candidate, services)) ?? [];
  const signature = matches.map(match => match.id).join(',');
  const [dismissedSignature, setDismissedSignature] = useState<string | null>(
    null
  );

  if (
    matches.length === 0 ||
    typedName.trim() === '' ||
    signature === dismissedSignature
  ) {
    return null;
  }

  return (
    <ResourceDuplicatesFooter
      matches={matches}
      typedName={typedName}
      organizationId={organizationId}
      workspaces={workspaces}
      getProfileHref={match => routes.contacts.detail(match.id)}
      onDismiss={() => setDismissedSignature(signature)}
      services={services}
    />
  );
}
